import { useState, useEffect, useCallback, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  mutationFunctions as stffMutationFunctions,
  sassMutationFunctions,
  STFF,
  SASS,
  sclrMutationFunctions,
} from "@/models/stff";

import { MutationResponse, LeaDataModel, SCLR } from "@/models";
import {
  FileSubmission,
  FileStatusEnum,
  ValidationError,
} from "@/models/file-submission";
import { useFileSubmissionsData, useFileSubmissionErrors } from "@/queries";
import { MAX_ASSIGNMENTS, MAX_CLASSROOMS } from "../hooks/useAddStaffWizard";

type SkipFlags = {
  skipAssignment: boolean;
  skipClassroom: boolean;
  activeAssignmentCount: number;
  activeClassroomCount: number;
};

type Forms = {
  stffForm: UseFormReturn<STFF>;
  assignmentForms: UseFormReturn<SASS>[];
  classroomForms: UseFormReturn<SCLR>[];
};

type SubmissionResult = {
  stff: MutationResponse<STFF> | null;
  sasses: Array<MutationResponse<SASS> | null>;
  sclrs: Array<MutationResponse<SCLR> | null>;
};

type SubmissionCallbacks = {
  onSubmitting?: () => void;
  onStffPosted?: () => void;
  onComplete?: () => void;
  onStffFailed?: (errors: ValidationError<keyof STFF>[]) => void;
  addPendingFileSubmission?: (fs: FileSubmission) => void;
};

type SubmissionPhase =
  | "idle"
  | "submitting-stff"
  | "waiting-for-stff"
  | "submitting-related"
  | "complete";

export function useStaffSubmission(
  forms: Forms,
  skipFlags: SkipFlags,
  leaData: LeaDataModel,
  esiIdPath: string,
  callbacks?: SubmissionCallbacks
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phase, setPhase] = useState<SubmissionPhase>("idle");
  const [stffFileSubmissionId, setStffFileSubmissionId] = useState<
    string | null
  >(null);
  const [failedSubmissionId, setFailedSubmissionId] = useState<string | null>(
    null
  );

  const addedSubmissionIds = useRef<Set<string | number>>(new Set());

  const [results, setResults] = useState<SubmissionResult>({
    stff: null,
    sasses: Array(MAX_ASSIGNMENTS).fill(null),
    sclrs: Array(MAX_CLASSROOMS).fill(null),
  });

  // Result setters
  const setStffResult = (r: MutationResponse<STFF>) => {
    setResults((prev) => ({ ...prev, stff: r }));
  };

  const setSassResultAt = (index: number) => (r: MutationResponse<SASS>) => {
    setResults((prev) => {
      const next = [...prev.sasses];
      next[index] = r;
      return { ...prev, sasses: next };
    });
  };

  const setSclrResultAt = (index: number) => (r: MutationResponse<SCLR>) => {
    setResults((prev) => {
      const next = [...prev.sclrs];
      next[index] = r;
      return { ...prev, sclrs: next };
    });
  };

  // Set up mutation functions
  const stffMutateFns = stffMutationFunctions(leaData, setStffResult);
  const sassMutateFnsArray = [
    sassMutationFunctions(leaData, setSassResultAt(0)),
    sassMutationFunctions(leaData, setSassResultAt(1)),
    sassMutationFunctions(leaData, setSassResultAt(2)),
    sassMutationFunctions(leaData, setSassResultAt(3)),
    sassMutationFunctions(leaData, setSassResultAt(4)),
    sassMutationFunctions(leaData, setSassResultAt(5)),
    sassMutationFunctions(leaData, setSassResultAt(6)),
    sassMutationFunctions(leaData, setSassResultAt(7)),
    sassMutationFunctions(leaData, setSassResultAt(8)),
    sassMutationFunctions(leaData, setSassResultAt(9)),
  ];
  const sclrMutateFnsArray = [
    sclrMutationFunctions(leaData, setSclrResultAt(0)),
    sclrMutationFunctions(leaData, setSclrResultAt(1)),
    sclrMutationFunctions(leaData, setSclrResultAt(2)),
    sclrMutationFunctions(leaData, setSclrResultAt(3)),
    sclrMutationFunctions(leaData, setSclrResultAt(4)),
    sclrMutationFunctions(leaData, setSclrResultAt(5)),
    sclrMutationFunctions(leaData, setSclrResultAt(6)),
    sclrMutationFunctions(leaData, setSclrResultAt(7)),
    sclrMutationFunctions(leaData, setSclrResultAt(8)),
    sclrMutationFunctions(leaData, setSclrResultAt(9)),
  ];

  // Poll for file submission status when waiting for STFF to post
  const refreshInterval = phase === "waiting-for-stff" ? 3000 : 3600000;
  const submissionDataQuery = useFileSubmissionsData(
    esiIdPath,
    refreshInterval
  );

  // Fetch detailed errors when a submission fails
  const errorsQuery = useFileSubmissionErrors(
    esiIdPath,
    failedSubmissionId || "",
    !!failedSubmissionId
  );

  // Helper to add file submission only once
  const addSubmissionOnce = useCallback(
    (fs: FileSubmission | null | undefined) => {
      if (
        fs &&
        callbacks?.addPendingFileSubmission &&
        !addedSubmissionIds.current.has(fs.Id)
      ) {
        addedSubmissionIds.current.add(fs.Id);
        callbacks.addPendingFileSubmission(fs);
      }
    },
    [callbacks]
  );

  // Submit related records after STFF is posted
  const submitRelatedRecords = useCallback(async () => {
    setPhase("submitting-related");

    const localStaffId = forms.stffForm.getValues().LocalStaffId || "";

    if (!skipFlags.skipAssignment) {
      for (let i = 0; i < skipFlags.activeAssignmentCount; i++) {
        const sassData = forms.assignmentForms[i].getValues();
        sassData.LocalStaffId = localStaffId;
        await sassMutateFnsArray[i].handleSave(sassData);
      }
    }

    if (!skipFlags.skipClassroom) {
      for (let i = 0; i < skipFlags.activeClassroomCount; i++) {
        const sclrData = forms.classroomForms[i].getValues();
        sclrData.LocalStaffId = localStaffId;
        await sclrMutateFnsArray[i].handleSave(sclrData);
      }
    }

    setPhase("complete");
    setIsSubmitting(false);

    if (callbacks?.onComplete) {
      callbacks.onComplete();
    }
  }, [forms, skipFlags, sassMutateFnsArray, sclrMutateFnsArray, callbacks]);

  // Watch for STFF mutation result - start polling when we get the file submission ID
  useEffect(() => {
    if (!results.stff || phase !== "submitting-stff") return;

    if (results.stff.fileSubmission) {
      setStffFileSubmissionId(results.stff.fileSubmission.Id);
      setPhase("waiting-for-stff");
    }
  }, [results.stff, phase]);

  // Poll for STFF status - check if Posted or Failed
  useEffect(() => {
    if (phase !== "waiting-for-stff" || !stffFileSubmissionId) return;
    if (!submissionDataQuery.data) return;

    const stffSubmission = submissionDataQuery.data.find(
      (x) => x.Id === stffFileSubmissionId
    );

    if (!stffSubmission) return;

    if (stffSubmission.CurrentStatus === FileStatusEnum.Posted) {
      addSubmissionOnce(stffSubmission);

      if (callbacks?.onStffPosted) {
        callbacks.onStffPosted();
      }

      submitRelatedRecords();
    } else if (
      stffSubmission.CurrentStatus === FileStatusEnum.Failed ||
      stffSubmission.CurrentStatus === FileStatusEnum.Rejected
    ) {
      addSubmissionOnce(stffSubmission);
      setFailedSubmissionId(String(stffSubmission.Id));
      setPhase("idle");
      setIsSubmitting(false);
      setStffFileSubmissionId(null);
    }
  }, [
    phase,
    stffFileSubmissionId,
    submissionDataQuery.data,
    addSubmissionOnce,
    submitRelatedRecords,
    callbacks,
  ]);

  // Process detailed errors when they are fetched
  useEffect(() => {
    if (!failedSubmissionId || !errorsQuery.data) return;

    const errors = errorsQuery.data;

    if (errors.length > 0) {
      errors.forEach((e) => {
        forms.stffForm.setError((e.Fieldname || "root") as any, {
          type: "manual",
          message: e.Message || "Validation error",
        });
      });
    }

    if (callbacks?.onStffFailed) {
      if (errors.length > 0) {
        callbacks.onStffFailed(errors as any);
      } else {
        callbacks.onStffFailed([
          {
            Message:
              "STFF validation failed. Please check the View Submissions page for details.",
          },
        ] as any);
      }
    }

    setFailedSubmissionId(null);
  }, [failedSubmissionId, errorsQuery.data, forms.stffForm, callbacks]);

  // Watch for related record results - add to pending submissions
  useEffect(() => {
    if (phase !== "submitting-related" && phase !== "complete") return;

    const resultMappings = [
      ...results.sasses.map((result, i) => ({
        result,
        form: forms.assignmentForms[i],
      })),
      ...results.sclrs.map((result, i) => ({
        result,
        form: forms.classroomForms[i],
      })),
    ];

    resultMappings.forEach(({ result, form }) => {
      if (!form) return;
      if (result?.errors?.length) {
        result.errors.forEach((e: any) => {
          form.setError(e.Fieldname || e.fieldname, {
            type: "manual",
            message: e.Message || e.message,
          });
        });
      }
      addSubmissionOnce(result?.fileSubmission);
    });
  }, [phase, results.sasses, results.sclrs, forms, addSubmissionOnce]);

  // Main submit function
  const submitStaff = useCallback(
    async (isNewRecord: boolean) => {
      setIsSubmitting(true);
      setPhase("submitting-stff");
      setStffFileSubmissionId(null);
      setFailedSubmissionId(null);
      addedSubmissionIds.current.clear();

      setResults({
        stff: null,
        sasses: Array(MAX_ASSIGNMENTS).fill(null),
        sclrs: Array(MAX_CLASSROOMS).fill(null),
      });

      const isStffValid = await forms.stffForm.trigger();

      if (!isStffValid) {
        setIsSubmitting(false);
        setPhase("idle");
        return;
      }

      const stffData = forms.stffForm.getValues();

      if (isNewRecord) {
        delete (stffData as any).StaffUniqueId;
      }

      if (callbacks?.onSubmitting) {
        callbacks.onSubmitting();
      }

      try {
        await stffMutateFns.handleSave(stffData);
      } catch (error) {
        console.error("Error submitting STFF:", error);
        setIsSubmitting(false);
        setPhase("idle");
      }
    },
    [forms.stffForm, stffMutateFns, callbacks]
  );

  return {
    submitStaff,
    isSubmitting,
    phase,
    results,
  };
}
